/**
 * Help Service
 *
 * Purpose: Centralised Supabase queries for the help system
 * Created: March 24, 2026
 */

import { createLogger, getErrorMessage } from '@xanectech/lib';
import { supabase } from '@xanectech/supabase-client';
import type {
  HelpArticle,
  HelpCategory,
  HelpContextMapping,
  HelpFieldTip,
  HelpArticleRelation,
} from '@xanectech/types';

const logger = createLogger('HelpService');

export const HelpService = {
  /**
   * Fetch a single published article by slug
   */
  async getArticleBySlug(slug: string): Promise<HelpArticle | null> {
    const { data, error } = await supabase
      .from('help_articles')
      .select('*, category:help_categories(*)')
      .eq('slug', slug)
      .eq('is_published', true)
      .maybeSingle();

    if (error) {
      logger.error('getArticleBySlug error:', {
        error: getErrorMessage(error),
        slug,
      });
      return null;
    }

    return data as unknown as HelpArticle | null;
  },

  /**
   * Search published articles by query string
   */
  async searchArticles(query: string, app?: string): Promise<HelpArticle[]> {
    let q = supabase
      .from('help_articles')
      .select('*, category:help_categories(*)')
      .eq('is_published', true)
      .or(
        `title.ilike.%${query}%,content.ilike.%${query}%,description.ilike.%${query}%`,
      )
      .order('view_count', { ascending: false })
      .limit(20);

    // If app is provided, filter by category slugs that belong to that app
    // This is a soft filter — categories don't have an app column directly
    if (app) {
      // We filter via tags array containing the app name
      q = q.contains('tags', [app]);
    }

    const { data, error } = await q;

    if (error) {
      logger.error('searchArticles error:', {
        error: getErrorMessage(error),
        query,
        app,
      });
      return [];
    }

    return (data ?? []) as unknown as HelpArticle[];
  },

  /**
   * Fetch active category tree
   */
  async getCategoryTree(): Promise<HelpCategory[]> {
    const { data, error } = await supabase
      .from('help_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      logger.error('getCategoryTree error:', {
        error: getErrorMessage(error),
      });
      return [];
    }

    // Build tree structure
    const categories = (data ?? []) as unknown as HelpCategory[];
    const rootCategories = categories.filter((c) => !c.parent_id);
    const childMap = new Map<string, HelpCategory[]>();

    for (const cat of categories) {
      if (cat.parent_id) {
        const children = childMap.get(cat.parent_id) ?? [];
        children.push(cat);
        childMap.set(cat.parent_id, children);
      }
    }

    const attachChildren = (cat: HelpCategory): HelpCategory => ({
      ...cat,
      children: (childMap.get(cat.id) ?? []).map(attachChildren),
    });

    return rootCategories.map(attachChildren);
  },

  /**
   * Resolve context mapping for a given app and route
   */
  async getContextMapping(
    app: string,
    routePattern: string,
    componentKey?: string,
  ): Promise<HelpContextMapping | null> {
    let q = supabase
      .from('help_context_mappings')
      .select('*')
      .eq('app', app)
      .eq('is_active', true);

    if (componentKey) {
      q = q.eq('component_key', componentKey);
    }

    const { data, error } = await q;

    if (error) {
      logger.error('getContextMapping error:', {
        error: getErrorMessage(error),
        app,
        routePattern,
        componentKey,
      });
      return null;
    }

    if (!data || data.length === 0) return null;

    // Find best match: exact route first, then glob patterns
    const mappings = data as unknown as HelpContextMapping[];
    const exactMatch = mappings.find((m) => m.route_pattern === routePattern);
    if (exactMatch) return exactMatch;

    // Try glob matching (route_pattern ending with *)
    const globMatch = mappings.find((m) => {
      if (!m.route_pattern.endsWith('*')) return false;
      const prefix = m.route_pattern.slice(0, -1);
      return routePattern.startsWith(prefix);
    });

    return globMatch ?? null;
  },

  /**
   * Fetch a field tip by app and context key
   */
  async getFieldTip(
    app: string,
    contextKey: string,
  ): Promise<HelpFieldTip | null> {
    const { data, error } = await supabase
      .from('help_field_tips')
      .select('*')
      .eq('app', app)
      .eq('context_key', contextKey)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      logger.error('getFieldTip error:', {
        error: getErrorMessage(error),
        app,
        contextKey,
      });
      return null;
    }

    return data as HelpFieldTip | null;
  },

  /**
   * Submit feedback for an article
   */
  async submitFeedback(
    articleId: string,
    isHelpful: boolean,
    userId?: string,
  ): Promise<boolean> {
    const { error } = await supabase.from('help_article_feedback').insert({
      article_id: articleId,
      is_helpful: isHelpful,
      user_id: userId ?? null,
    });

    if (error) {
      logger.error('submitFeedback error:', {
        error: getErrorMessage(error),
        articleId,
        isHelpful,
        userId,
      });
      return false;
    }

    return true;
  },

  /**
   * Track a search term
   */
  async trackSearch(term: string): Promise<void> {
    const normalised = term.toLowerCase().trim();
    if (!normalised) return;

    // Upsert: increment count or insert new
    const { data: existing } = await supabase
      .from('help_popular_searches')
      .select('id, search_count')
      .eq('term', normalised)
      .maybeSingle();

    if (existing) {
      await supabase
        .from('help_popular_searches')
        .update({ search_count: existing.search_count + 1 })
        .eq('id', existing.id);
    } else {
      await supabase.from('help_popular_searches').insert({ term: normalised });
    }
  },

  /**
   * Get related articles for a given article
   */
  async getRelatedArticles(articleId: string): Promise<HelpArticle[]> {
    const { data, error } = await supabase
      .from('help_article_relations')
      .select(
        '*, target_article:help_articles!help_article_relations_target_article_id_fkey(*)',
      )
      .eq('source_article_id', articleId)
      .order('display_order', { ascending: true });

    if (error) {
      logger.error('getRelatedArticles error:', {
        error: getErrorMessage(error),
        articleId,
      });
      return [];
    }

    const relations = (data ?? []) as unknown as (HelpArticleRelation & {
      target_article: HelpArticle;
    })[];

    return relations
      .filter((r) => r.target_article?.is_published)
      .map((r) => r.target_article);
  },

  /**
   * Get popular/featured search terms
   */
  async getPopularSearches(limit = 10) {
    const { data, error } = await supabase
      .from('help_popular_searches')
      .select('*')
      .order('search_count', { ascending: false })
      .limit(limit);

    if (error) {
      logger.error('getPopularSearches error:', {
        error: getErrorMessage(error),
        limit,
      });
      return [];
    }

    return data ?? [];
  },

  /**
   * Fetch help article by Id
   */
  async getArticleById(articleId: string): Promise<HelpArticle | null> {
    const { data, error } = await supabase
      .from('help_articles')
      .select('*, category:help_categories(*)')
      .eq('id', articleId)
      .maybeSingle();

    if (error) {
      logger.error('getArticleById error:', {
        error: getErrorMessage(error),
        articleId,
      });
      return null;
    }

    return data as unknown as HelpArticle | null;
  },

  /**
   * Fetch all articles (for admin)
   */
  async getAllArticles(): Promise<HelpArticle[]> {
    const { data, error } = await supabase
      .from('help_articles')
      .select('*, category:help_categories(*)')
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('getAllArticles error:', {
        error: getErrorMessage(error),
      });
      return [];
    }

    return (data ?? []) as unknown as HelpArticle[];
  },

  /**
   * Fetch published articles belonging to a category by category slug
   */
  async getArticlesByCategorySlug(
    categorySlug: string,
  ): Promise<HelpArticle[]> {
    // First resolve category id from slug
    const { data: category, error: catError } = await supabase
      .from('help_categories')
      .select('id')
      .eq('slug', categorySlug)
      .eq('is_active', true)
      .maybeSingle();

    if (catError || !category) {
      logger.error('getArticlesByCategorySlug category error:', {
        error: getErrorMessage(catError),
        categorySlug,
      });
      return [];
    }

    const { data, error } = await supabase
      .from('help_articles')
      .select('*, category:help_categories(*)')
      .eq('category_id', category.id)
      .eq('is_published', true)
      .order('display_order', { ascending: true })
      .order('title', { ascending: true });

    if (error) {
      logger.error('getArticlesByCategorySlug error:', {
        error: getErrorMessage(error),
        categorySlug,
      });
      return [];
    }

    return (data ?? []) as unknown as HelpArticle[];
  },

  /**
   * Fetch a category by slug
   */
  async getCategoryBySlug(slug: string): Promise<HelpCategory | null> {
    const { data, error } = await supabase
      .from('help_categories')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      logger.error('getCategoryBySlug error:', {
        error: getErrorMessage(error),
        slug,
      });
      return null;
    }

    return data as unknown as HelpCategory | null;
  },

  async createArticle(
    article: Partial<HelpArticle>,
  ): Promise<HelpArticle | null> {
    const { data, error } = await supabase
      .from('help_articles')
      .insert(article as HelpArticle)
      .single();

    if (error) {
      logger.error('createArticle error:', {
        error: getErrorMessage(error),
        article: article.title,
      });
      return null;
    }

    return data as unknown as HelpArticle;
  },

  async updateArticle(
    articleId: string,
    updates: Partial<HelpArticle>,
  ): Promise<HelpArticle | null> {
    const { data, error } = await supabase
      .from('help_articles')
      .update(updates)
      .eq('id', articleId)
      .single();

    if (error) {
      logger.error('updateArticle error:', {
        error: getErrorMessage(error),
        articleId,
        updates,
      });
      return null;
    }

    return data as unknown as HelpArticle;
  },

  async deleteArticle(articleId: string): Promise<boolean> {
    const { error } = await supabase
      .from('help_articles')
      .delete()
      .eq('id', articleId);

    if (error) {
      logger.error('deleteArticle error:', {
        error: getErrorMessage(error),
        articleId,
      });
      return false;
    }

    return true;
  },
};
